/**
 * Documentation-app client entry (internal id: `vault`). Imported once by
 * `core/App.tsx` for its side effect — registers a lock callback so the
 * decrypted attachment blob cache is purged whenever the user locks (or
 * auto-lock fires) AND a sign-out prep callback so durable state is
 * flushed and IndexedDB is wiped before keys are zeroed. No default/named
 * exports; the Workspace component is imported directly by core/App.tsx.
 */
import {
  registerLockCallback,
  registerPendingCountProvider,
  registerSignOutPrep,
} from '../../core/lib/vault';
import { purgeAttachmentBlobCache } from './lib/attachmentBlobCache';
import { flushAllLiveDocs, releaseAllLiveDocs } from './lib/yjsCollab';
import { flushOutbox, getPendingCounts } from './sync';
import { localStore } from './db';

// Scrub the decrypted attachment blob cache when the user locks. Core
// fires every registered lock callback before zeroing keys.
registerLockCallback(purgeAttachmentBlobCache);

// Release every in-memory Y.Doc on lock so it can't be reused with stale
// pre-lock body on the next unlock. Fire-and-forget — Core's lock loop is
// synchronous; the async chain captures each state's CryptoKey ref before
// vault.lock() nulls the global vaultKey, so the flush still succeeds.
// See plan F3.
registerLockCallback(() => {
  void releaseAllLiveDocs().catch((err) => {
    console.warn('[vault] releaseAllLiveDocs on lock failed', err);
  });
});

// Surface "N edits waiting" on the unlock screen. Reads the same IDB
// counts useSyncStatus aggregates — the tech sees a banner instead of an
// unexplained prompt and won't close the tab on their pending work.
registerPendingCountProvider(async () => {
  const counts = await getPendingCounts().catch(() => null);
  if (!counts) return 0;
  return counts.pendingDocCount + counts.pendingCustomerCount + counts.outboxOpCount;
});

// Sign-out prep: best-effort flush of any in-flight edits + outbox; report
// any remaining pending state so the user is asked before we discard it;
// wipe IndexedDB on confirmation. Without the wipe, a `pendingSync:true`
// row would survive the sign-out/sign-in cycle and ghost the next session.
registerSignOutPrep(async () => {
  // Best-effort flush — bound by a timeout so a hung PUT doesn't block the
  // sign-out indefinitely. The pendingCount reported below reflects the
  // post-flush state.
  const FLUSH_TIMEOUT_MS = 5_000;
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS));
  await Promise.race([
    Promise.allSettled([flushAllLiveDocs(), flushOutbox()]).then(() => undefined),
    timeout,
  ]);
  const counts = await getPendingCounts().catch(() => ({
    pendingDocCount: 0,
    pendingCustomerCount: 0,
    outboxOpCount: 0,
  }));
  const pendingCount =
    counts.pendingDocCount + counts.pendingCustomerCount + counts.outboxOpCount;
  return {
    pendingCount,
    description: pendingCount === 1 ? '1 unsynced edit' : `${pendingCount} unsynced edits`,
    wipe: () => localStore.wipe(),
  };
});
